import React, { FC, useCallback, useContext, useMemo } from 'react';
import { ProductToolsFetcher } from '../../fetchers/ProductTools';
import { UrlQueryDataGrid } from '../UrlQueryDataGrid/UrlQueryDataGrid';
import { GridColumns, GridRowParams } from '@mui/x-data-grid';
import { useAutoFetch } from '../../contexts/Fetcher';
import { AddProductToolDialog } from './AddProductToolDialog';
import { EditProductToolDialog } from './EditProductToolDialog';
import { useLocation } from 'react-router-dom';
import { useDefaultSorting } from '../../hooks/useDefaultSorting';
import { useTranslation } from 'react-i18next';
import { BoolChip } from '../BoolChip/BoolChip';
import { getBoolFilterOperator } from '../../filterOperators/boolFilterOperator';
import { useDialog } from '../../hooks/useDialog';
import { useCloseDialog } from '../../hooks/useCloseDialog';
import { apiProductTool } from '../../resources/api/apiProductTool';
import { useQueryParam } from '../../hooks/useQueryParam';
import { useResourceChecker } from '../../resources/resource';
import { queryUrl } from '../../hooks/useQueryUrl';
import { CollapsedContentsWithTooltip } from '../CollapsedContentsWithTooltip/CollapsedContentsWithTooltip';
import { IProductPlanPackageTool } from '../../models/ProductPlanPackageTool';

const { useUpdateTool } = apiProductTool;

export const ProductTools: FC = () => {
  const { data } = useContext(ProductToolsFetcher.Context);
  useAutoFetch(ProductToolsFetcher);

  const { isOpen: isAdding, close: closeDialog } = useDialog('new');

  const abortEdit = useCloseDialog(void 0, ['edit']);

  const id = useQueryParam('edit');
  const { resource: resourceUpdate } = useUpdateTool('');
  const resourceChecker = useResourceChecker();

  const { search } = useLocation();

  useDefaultSorting('name', 'asc');
  const { t } = useTranslation();

  const columns = useMemo<GridColumns>(
    () => [
      {
        field: 'name',
        headerName: t('common.tableHeader.name'),
        flex: 1,
      },
      {
        field: 'isAddon',
        headerName: t('adminProducts.tools.table.isAddonHeader'),
        width: 120,
        renderCell: BoolChip,
        filterOperators: [getBoolFilterOperator()],
      },
      {
        field: 'archived',
        headerName: t('common.tableHeader.archived'),
        width: 120,
        renderCell: BoolChip,
        filterOperators: [getBoolFilterOperator()],
      },
      {
        field: 'bundledTools',
        headerName: t('adminProducts.tools.table.bundledToolsHeader'),
        flex: 1,
        sortable: false,
        filterable: false,
        valueGetter: (params) =>
          t('adminProducts.tools.table.totalBundledTools', { count: params.row.bundledTools.length }),
        renderCell: (params) => {
          const items = (params.row.bundledTools as IProductPlanPackageTool['bundledTools']).map(({ name }) => ({
            name,
          }));
          return <CollapsedContentsWithTooltip items={items}>{params.value}</CollapsedContentsWithTooltip>;
        },
      },
    ],
    []
  );

  const getRowLink = useCallback((params: GridRowParams) => queryUrl('edit', params.row.id, search), [search]);

  const isGetRowLinkAllowed = resourceChecker(resourceUpdate);

  if (!data) return null;
  const tool = data.tools.find((t) => t.id === id);

  return (
    <div>
      <UrlQueryDataGrid
        columns={columns}
        rows={data.tools}
        disableExtendRowFullWidth={false}
        autoHeight={true}
        disableSelectionOnClick={true}
        getRowLink={isGetRowLinkAllowed ? getRowLink : void 0}
      />
      <AddProductToolDialog open={isAdding} closeDialog={closeDialog} />
      <EditProductToolDialog tool={tool} closeDialog={abortEdit} />
    </div>
  );
};
